import clsx from 'clsx';
import type { QueueSummary } from '../lib/api';
import { formatCount, formatRate } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import { Badge, Pill, TONE_TEXT, type Tone } from './Pill';

interface Props {
  queue: QueueSummary;
  onClick?: () => void;
}

function Stat({
  label,
  value,
  tone = 'gray',
}: {
  readonly label: string;
  readonly value: number;
  readonly tone?: Tone;
}) {
  const { locale } = useLocale();

  return (
    <div className="text-center">
      <p
        className={clsx(
          'text-lg sm:text-2xl font-bold tabular-nums',
          TONE_TEXT[tone],
        )}
      >
        {formatCount(value, locale)}
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

export function QueueCard({ queue, onClick }: Props) {
  const { t, locale } = useLocale();
  const hasFailures = queue.failed > 0;
  const isHighFailureRate = queue.failureRate > 10;

  return (
    <div
      onClick={onClick}
      className={clsx(
        // `.card` is the landing page's recipe verbatim, including its hover shadow — which is
        // exactly right here, because this card IS a link to the queue's detail page.
        'card',
        onClick && 'cursor-pointer',
        // The border is the only per-state signal on the card itself; the counts below carry
        // the detail. A card whose whole surface turned red would drown its own numbers.
        hasFailures && !queue.paused
          ? 'border-red-200'
          : queue.paused
            ? 'border-amber-200'
            : 'border-gray-200',
      )}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm break-all">
            {queue.name}
          </h3>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Pill
              tone={
                queue.paused ? 'amber' : queue.active > 0 ? 'green' : 'gray'
              }
            >
              {t(
                queue.paused
                  ? 'queueState.paused'
                  : queue.active > 0
                    ? 'queueState.running'
                    : 'queueState.idle',
              )}
            </Pill>
            {isHighFailureRate && (
              <Badge tone="red">
                {t('queueCard.failureBadge', {
                  rate: formatRate(queue.failureRate, locale),
                })}
              </Badge>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500">
            {t('queueCard.processingRate')}
          </p>
          <p className="text-sm font-semibold text-gray-900 tabular-nums">
            {t('queueCard.perMinute', {
              rate: formatRate(queue.processingRate, locale),
            })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 pt-4 border-t border-gray-100">
        <Stat
          label={t('queueStats.waiting')}
          value={queue.waiting}
          tone="sky"
        />
        <Stat
          label={t('queueStats.active')}
          value={queue.active}
          tone="green"
        />
        <Stat
          label={t('queueStats.delayed')}
          value={queue.delayed}
          tone="amber"
        />
        <Stat
          label={t('queueStats.completed')}
          value={queue.completed}
          tone="gray"
        />
        <Stat
          label={t('queueStats.failed')}
          value={queue.failed}
          tone={hasFailures ? 'red' : 'gray'}
        />
      </div>
    </div>
  );
}
